/**
 * AI 트래커 '개념 추이' 시계열 변환 (순수 함수).
 *
 * 일별 재생 envelope(= aitrackerTimelineCache 로 구축된 날짜별 프레임 모음)에서
 * 선택 개념들의 지표(기본 r=직접 개념 숙련도) 시계열을 recharts LineChart 용 행으로 변환한다.
 *
 * envelope = { conceptIndex: [cid...], vScale, dates: ['yyyy-mm-dd'...], frames: { date: {r:[],h:[],s:[]} } }
 *   - frames[date][metric][i] 는 conceptIndex[i] 개념의 양자화 정수값. 실제값 = 값 / vScale (decodeQ 와 동일).
 *   - 개념이 특정 시점에 미등장(엔진 좌표 없음)이면 값이 null → 라인 결측(연결하지 않음).
 */

/**
 * @param {object|null} envelope - { conceptIndex, vScale, dates, frames }
 * @param {Array<string|number>} cids - 시계열로 그릴 개념 cid 목록(중복·미존재 개념은 정리됨)
 * @param {'r'|'h'|'s'} [metric='r'] - 프레임 지표 키
 * @returns {{ rows: Array<{date:string,[key:string]:number|null}>, keys: string[] }}
 *   rows: 날짜순 데이터 포인트(키=String(cid)), keys: 실제 그릴 라인 dataKey(문자열 cid, 원래 선택 순서 유지)
 */
export function buildConceptSeries(envelope, cids, metric = 'r') {
  if (!envelope || !Array.isArray(cids) || cids.length === 0) return { rows: [], keys: [] };
  const { conceptIndex = [], vScale, dates = [], frames = {} } = envelope;
  const scale = vScale || 10000;

  // cid → 프레임 배열 인덱스
  const idxByCid = new Map();
  conceptIndex.forEach((cid, i) => idxByCid.set(String(cid), i));

  // 선택 순서 유지 + 중복 제거 + envelope 에 없는 개념 제외
  const keys = [];
  const seen = new Set();
  cids.forEach((cid) => {
    const k = String(cid);
    if (!seen.has(k) && idxByCid.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  });

  const rows = dates.map((date) => {
    const frame = frames[date];
    const arr = frame ? frame[metric] : null;
    const row = { date };
    keys.forEach((k) => {
      const i = idxByCid.get(k);
      const q = arr && i != null ? arr[i] : undefined;
      row[k] = q === null || q === undefined ? null : q / scale;
    });
    return row;
  });

  return { rows, keys };
}

/**
 * 선택이 없을 때 자동 표시할 '변화량 큰' 개념 상위 N.
 * 각 개념의 날짜순 non-null 값에서 |마지막 − 처음| 을 변화량으로 보고 내림차순 상위 limit 개.
 * non-null 값이 2개 미만인 개념(라인 못 그림)은 제외. 동률은 cid 오름차순으로 안정 정렬.
 * @param {object|null} envelope
 * @param {number} [limit=12]
 * @param {'r'|'h'|'s'} [metric='r']
 * @returns {string[]} cid(문자열) 목록
 */
export function topMoversByDelta(envelope, limit = 12, metric = 'r') {
  if (!envelope) return [];
  const { conceptIndex = [], vScale, dates = [], frames = {} } = envelope;
  const scale = vScale || 10000;

  const scored = [];
  conceptIndex.forEach((cid, i) => {
    let first = null;
    let last = null;
    let count = 0;
    for (let d = 0; d < dates.length; d += 1) {
      const frame = frames[dates[d]];
      const arr = frame ? frame[metric] : null;
      const q = arr ? arr[i] : undefined;
      if (q === null || q === undefined) continue;
      const v = q / scale;
      if (first === null) first = v;
      last = v;
      count += 1;
    }
    if (count >= 2) scored.push({ cid: String(cid), delta: Math.abs(last - first) });
  });

  // eslint-disable-next-line no-nested-ternary
  scored.sort((a, b) => b.delta - a.delta || (a.cid < b.cid ? -1 : a.cid > b.cid ? 1 : 0));
  return scored.slice(0, limit).map((s) => s.cid);
}
